import { useId, type ReactNode } from 'react'

import { cn } from './cn'
import { useQuietField } from './control-scope'

/** What a field shows in place of a value it does not have. */
export const EMPTY_MARK = '—'

/** The mark on its own, for a value that is read rather than typed. */
export const dash = <span className="text-faint">{EMPTY_MARK}</span>

type FieldProps = {
  label?: string
  /** One line under the control explaining what it accepts. */
  helper?: string
  /** Replaces `helper` and turns the control's border red. */
  error?: string
  className?: string
  /** Receives the id and the error state so the control can wire itself up. */
  children: (props: {
    id: string
    'aria-describedby': string | undefined
    'aria-invalid': boolean
  }) => ReactNode
}

/**
 * A labelled control.
 *
 * The label is a real `<label htmlFor>`, so clicking it focuses the control and
 * a screen reader announces the pair. The render prop is what carries the
 * generated id down without every call site inventing one.
 *
 * Plumbing rather than a component a screen reaches for: every control in
 * `base/` already renders one around itself, and that is the only way it
 * should be met. A screen that wants a labelled control asks the control for
 * the label.
 */
export function Field({
  label,
  helper,
  error,
  className,
  children,
}: FieldProps) {
  const id = useId()
  const noteId = error || helper ? `${id}-note` : undefined

  return (
    <div className={cn('flex flex-col gap-1', className)}>
      {label ? (
        <label htmlFor={id} className="min-h-5 text-[12.5px] text-foreground-2">
          {label}
        </label>
      ) : null}

      {children({
        id,
        'aria-describedby': noteId,
        'aria-invalid': !!error,
      })}

      {(error || helper) && (
        <span
          id={noteId}
          className={cn(
            'text-[11.5px]',
            error ? 'text-destructive' : 'text-faint',
          )}
        >
          {error || helper}
        </span>
      )}
    </div>
  )
}

/** The control's own shell — one border, one focus ring, one disabled look. */
const controlClass = cn(
  'w-full rounded-md border border-border bg-card text-foreground',
  'px-[var(--control-padding-x)] text-[length:var(--control-text)]',
  'placeholder:text-faint transition-colors',
  'focus:outline-none focus:border-primary/60 focus:ring-2 focus:ring-primary/15',
  'disabled:bg-line-soft disabled:text-faint',
  'aria-invalid:border-destructive aria-invalid:ring-destructive/20',
)

const quietOverrides = cn(
  'border-transparent bg-transparent px-1.5 h-7',
  'hover:border-border hover:bg-card focus:bg-card',
  'disabled:bg-transparent disabled:hover:border-transparent disabled:hover:bg-transparent',
)

/**
 * The shell every control wears — one border, one focus ring, one height.
 *
 * `quiet` drops the box until the control is pointed at, which is what turns a
 * column of fields into something readable: the value reads as text, and an
 * empty one is a gap rather than another empty box.
 */
export function controlShell(quiet: boolean, extra?: string) {
  return cn(
    controlClass,
    quiet ? quietOverrides : 'h-[var(--control-height)]',
    extra,
  )
}

/**
 * A datasheet value whose editor is a dialog.
 *
 * It wears the same quiet shell as the inputs beside it, so the row reads as a
 * value that can be pressed rather than as a control of a different kind. A
 * `Button` here is the wrong shape: it brings its own height, padding and
 * ground, which is what made the one row a dialog owns stop looking like the
 * eight around it.
 *
 * @single-use moving it out would leave the caller hand-rolling a styled
 * `<button>` around `controlShell`, which the shape rules reject
 */
export function ValueButton({
  value,
  onClick,
  quiet: quietProp,
}: {
  /** Empty draws the same dash an empty quiet input shows. */
  value?: string | null
  onClick: () => void
  quiet?: boolean
}) {
  const quiet = useQuietField(quietProp)

  return (
    <button
      type="button"
      onClick={onClick}
      className={controlShell(quiet, 'flex items-center text-left')}
    >
      {value || dash}
    </button>
  )
}
